import { sessionManager } from "@/broadcastChannel/sessionManager";
import { refreshCachedDataAfterMutation } from "@/lib/queryClient";
import { useCallback, useState } from "react";

interface PostMultipartOptions<T> {
  url: string;
  data: T;
}

interface PostMultipartResponse {
  success: boolean;
  message?: string;
  data?: any;
}

interface UsePostMultipartResult<T> {
  postMultipart: (options: PostMultipartOptions<T>) => Promise<any>;
  loading: boolean;
  error?: string;
  response?: PostMultipartResponse;
}

function usePostMultipart<T>(): UsePostMultipartResult<T> {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [response, setResponse] = useState<PostMultipartResponse | undefined>(
    undefined,
  );

  const postMultipart = useCallback(
    async ({ url, data }: PostMultipartOptions<T>): Promise<any> => {
      setLoading(true);
      setError(undefined);
      setResponse(undefined);

      const token =
        localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

      try {
        const headers: HeadersInit = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const res = await fetch(url, {
          method: "POST",
          headers,
          body: data,
        });

        const contentType = res.headers.get("content-type") ?? "";
        const rawBody = await res.text();
        const result =
          contentType.includes("application/json") && rawBody
            ? JSON.parse(rawBody)
            : null;

        if (res.status === 401) {
          sessionManager.logout();
        }

        if (!res.ok) {
          throw new Error(
            result?.message ||
              result?.detail ||
              (rawBody && !contentType.includes("text/html") ? rawBody : "") ||
              "Une erreur est survenue",
          );
        }

        setResponse({
          success: true,
          message: result?.message,
          data: result,
        });
        void refreshCachedDataAfterMutation();

        return result;
      } catch (err: any) {
        setError(err.message || "An unknown error occurred");
        setResponse({
          success: false,
          message: err.message,
        });

        return {
          success: false,
          message: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { postMultipart, loading, error, response };
}

export default usePostMultipart;
